<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form v-slot="{ values, resetForm }" :initial-values="initial_values" :validation-schema="schema" @submit="updateGraph">
          <div class="gap-2 space-y-4">
            <div class="flex items-center justify-between gap-1 rounded-md shadow-sm">
              <TextInput
                :disabled="is_loading"
                :label="$t('Graph Name')"
                classes="w-3/6"
                input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :show-label="true"
                :placeholder="$t('Ex: Closed deals')"
                @update:modelValue="formDataChanged(values)" />

              <SelectInput
                :disabled="is_loading"
                name="graph_type"
                classes="w-4/6"
                :show-label="true"
                :allow-null="false"
                :items="graph_types"
                label-attr="name"
                value-attr="value"
                :placeholder="$t('Chart of your graph')"
                :label="$t('Chart type')"
                value=""
                input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @update:currentItem="formDataChanged(values)" />
              <SelectInput
                :items="public_private"
                label-attr="name"
                value-attr="value"
                :allow-null="false"
                name="is_public"
                :placeholder="$t('Public or private graph')"
                :show-label="true"
                :label="$t('Type')"
                classes="w-2/6"
                input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @update:currentItem="formDataChanged(values)" />
            </div>

            <div v-if="selected_series?.length" class="flex justify-between">
              <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text"> {{ $t("Series") }} </label>
              <div class="flex flex-row items-center gap-2">
                <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text">{{ $t("Aggregate") }} </label>

                <Switch
                  v-model="aggregate"
                  :disabled="is_loading"
                  :class="[
                    aggregate ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      aggregate && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : aggregate
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
              </div>
            </div>
            <div v-for="(serie, series_index) in selected_series" :key="serie" class="flex w-full gap-2">
              <SelectInput
                v-model="serie.metric"
                :value="serie.metric"
                :disabled="is_loading"
                :floating-label="true"
                :show-label="true"
                :allow-null="false"
                :items="series_metrics"
                :name="`serie-${series_index}`"
                value-attr="id"
                label-attr="name"
                :placeholder="$t('Aggregation method')"
                :label="$t('Serie')"
                classes="w-full"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                @update:currentItem="formDataChanged(values)" />
              <SelectInput
                v-if="values.graph_type == 'mixed_charts'"
                v-model="serie.chart_type"
                :value="serie.chart_type"
                :disabled="is_loading"
                :show-label="true"
                :allow-null="false"
                :items="chart_types"
                :name="`charttype-${series_index}`"
                value-attr="value"
                label-attr="name"
                :placeholder="$t('Chart type')"
                label=""
                classes="w-1/2"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
              <SelectInput
                v-if="aggregate"
                v-model="serie.aggregation_method"
                :value="serie.aggregation_method"
                :disabled="is_loading"
                :show-label="true"
                :allow-null="false"
                :items="metrics_aggregation_methods"
                :name="`agmeth-${series_index}`"
                value-attr="value"
                label-attr="name"
                :placeholder="$t('Aggregation method')"
                label=""
                classes="w-1/2"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                @update:modelValue="handleSelectAggregation($event, series_index)" />

              <div
                v-show="
                  !(
                    values.graph_type == 'pie' ||
                    values.graph_type == 'polarArea' ||
                    values.graph_type == 'radialBar' ||
                    values.graph_type == 'treemap'
                  )
                "
                class="h-10 w-10 !p-0 flex items-center has-tooltip">
                <ColorPickerList v-model:current-item="serie.space_color" />

                <span class="mb-10 ml-5 text-sm font-medium text-gray-700 tooltip whitespace-nowrap group-hover:bg-black">
                  <RotateCw
                    class="self-center w-5 h-5 text-gray-600"
                    aria-hidden="true"
                    @click="
                      () => {
                        serie.space_color = $store.state.data.graphs_default_colors[series_index]
                      }
                    " />
                </span>
              </div>
              <button
                v-if="selected_series?.length"
                type="button"
                class="m-0 border-transparent cancel-btn"
                @click="
                  () => {
                    selected_series.splice(series_index, 1)
                  }
                ">
                <Trash2 class="self-center w-5 h-5 text-red-400" aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              class="!mb-3 cancel-btn float-right"
              @click="
                () => {
                  selected_series.push({
                    id: selected_series.length + 1,
                    metric: series_metrics[0].id,
                    aggregation_method: 'average',
                    chart_type: chart_types[0].value,
                    space_color: $store.state.data.graphs_default_colors[selected_series.length]
                  })
                }
              ">
              {{ $t("Add Serie") }}
            </button>

            <div v-if="selected_series?.length && $utils.isNotEmpty(selected_series[0].metric)" class="flex flex-col w-full gap-1">
              <label class="block mb-1 text-base font-medium text-gray-700 dark:text-dark-body-text"> {{ $t("Set X-Axis") }} </label>
              <div class="flex w-full gap-1 mb-2">
                <ComboBoxInput
                  classes="w-full"
                  :disabled="is_loading"
                  :items="axis_metrics"
                  :allow-null="false"
                  :show-label="true"
                  :floating-label="true"
                  name="axis"
                  label-attr="name"
                  value-attr="id"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Axis')"
                  @update:currentItem="formDataChanged(values)" />
                <ComboBoxInput
                  v-if="isDateBasedAxis(values)"
                  classes="w-2/6"
                  :disabled="is_loading"
                  :items="truncate_by_types"
                  :allow-null="false"
                  :show-label="true"
                  :floating-label="true"
                  name="truncate_by"
                  label-attr="name"
                  value-attr="value"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Truncate by')"
                  @update:currentItem="formDataChanged(values)" />
                <SelectInput
                  :disabled="is_loading"
                  name="order_sort"
                  classes="w-3/6"
                  :show-label="true"
                  :allow-null="false"
                  :items="order_sorts"
                  label-attr="name"
                  value-attr="value"
                  :label="$t('Order sort')"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  @update:currentItem="formDataChanged(values)" />

                <ComboBoxInput
                  classes="w-2/6"
                  :disabled="is_loading"
                  :items="max_limits"
                  :show-label="true"
                  :allow-null="true"
                  :return-value="true"
                  :allow-typed-value="true"
                  :floating-label="true"
                  name="max_limit"
                  label-attr="name"
                  value-attr="value"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Top') + ' #'"
                  @update:currentItem="formDataChanged(values)" />
              </div>

              <div v-if="isDateBasedAxis(values)" class="flex w-full gap-1 mb-2">
                <DatePicker
                  name="from_date"
                  :value="initial_values['from_date']"
                  :placeholder="$t('From date')"
                  format="YYYY-MM-DD"
                  class="w-4/12 bg-white dark:bg-dark-screen-background" />
                <DatePicker
                  name="to_date"
                  :value="initial_values['to_date']"
                  :placeholder="$t('To date')"
                  format="YYYY-MM-DD"
                  class="w-4/12 bg-white dark:bg-dark-screen-background" />
              </div>

              <div
                v-if="(values['type'] == 'horizontal_bars' || values['type'] == 'vertical_bars') && selected_series?.length > 1"
                class="flex flex-col w-1/2 gap-2">
                <label class="block text-base font-medium text-gray-700">{{ $t("Stacked Bars") }}</label>

                <Switch
                  v-model="stacked_bars"
                  :class="[
                    stacked_bars ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      stacked_bars && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : stacked_bars
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
              </div>
              <div
                :class="[filter_error ? 'border-red-400' : '']"
                class="w-full p-2 rounded-md flex flex-col border-[1px] border-gray-300 dark:border-dark-divider-border pb-10">
                <div>{{ $t("Graph filter") }}:</div>

                <AdvancedMetricsFilterForm
                  :object-type="graph_object_type"
                  :initial-filter="filter"
                  @update="
                    ($event) => {
                      handleUpdateFilter($event)
                      formDataChanged(values)
                    }
                  " />

                <span v-if="filter_error" class="text-red-400">{{ $t("Filter is invalid") }}</span>
              </div>
            </div>

            <div class="flex flex-row justify-between w-full gap-2">
              <div class="w-1/2 m-0 cancel-btn" @click="is_loading ? null : $emit('cancel')">
                {{ $t("Cancel") }}
              </div>
              <button
                :class="[!selected_series?.length ? 'opacity-50' : '']"
                :disable="selected_series?.length == 0"
                type="button"
                class="w-1/2 primary-btn"
                @click="updateGraph(values, resetForm)">
                {{ is_loading ? $t("Loading...") : $t("Update Graph") }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script>
import { Switch } from "@headlessui/vue"
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import { Trash2, RotateCw } from "lucide-vue-next"
import ComboBoxInput from "../shared/ComboBoxInput.vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import ColorPickerList from "@/components/shared/ColorPickerList.vue"
import DatePicker from "@/components/shared/DatePicker.vue"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { useI18n } from "vue-i18n"

export default {
  components: {
    DatePicker,
    ColorPickerList,
    ComboBoxInput,
    AdvancedMetricsFilterForm,
    Switch,
    TextInput,
    SelectInput,
    Trash2,
    RotateCw,
    Form
  },
  props: {
    graph: {
      type: Object,
      required: true
    },
    pageReference: {
      type: String,
      required: true
    }
  },
  emits: ["cancel", "submitted", "done", "setComponentData"],
  setup(props) {
    const { t } = useI18n()
    let initial_values = {}
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Graph name is required")),
      graph_type: Yup.string().required(),
      is_public: Yup.mixed().oneOf(["t", "f"]),
      series: Yup.array().of(
        Yup.object().shape({
          metric: Yup.string().required(t("Metric is required")),
          aggregation_method: Yup.string().required(t("Aggregation method is required")),
          space_color: Yup.string().required(t("Color is required"))
        })
      ),
      max_limit: Yup.number(t("Number is required")).nullable(),
      axis: Yup.string().nullable().required(t("Graph axis is required")),
      aggregate: Yup.mixed().oneOf(["t", "f"]),
      order_sort: Yup.string(),
      from_date: Yup.date().nullable(),
      to_date: Yup.date().nullable()
    })
    if (props.graph) {
      initial_values = {
        id: props.graph.id,
        name: props.graph.name,
        truncate_by: props.graph.truncate_by,
        graph_type: props.graph.graph_type,
        is_public: props.graph.is_public ? "t" : "f",
        axis: props.graph.axis?.id,
        max_limit: props.graph.max_limit,
        order_sort: props.graph.order_sort,
        aggregate: props.graph.aggregate,
        from_date: props.graph.from_date,
        to_date: props.graph.to_date
      }
      if (!props.graph.axis && props.graph.axis_field) {
        initial_values["axis"] = props.graph.axis_field
      }
    }

    return {
      schema,
      initial_values
    }
  },
  data() {
    return {
      public_private: [
        { name: this.$t("Public"), value: "t" },
        { name: this.$t("Private (Me)"), value: "f" }
      ],
      graph_object_type: null,
      graph_types: [
        { name: this.$t("Horizontal Bars"), value: "horizontal_bars" },
        { name: this.$t("Vertical Bars"), value: "vertical_bars" },
        { name: this.$t("Patterned Bars"), value: "patterned_bars" },
        { name: this.$t("Stacked Horizontal Bars"), value: "stacked_horizontal_bars" },
        { name: this.$t("Stacked Vertical Bars"), value: "stacked_vertical_bars" },
        { name: this.$t("100% Stacked Bars"), value: "100_stacked_bars" },
        { name: this.$t("Table"), value: "table" },
        { name: this.$t("Line"), value: "line" },
        { name: this.$t("Dashed Line"), value: "dashed_line" },
        { name: this.$t("Brush"), value: "brush" },
        { name: this.$t("Pie"), value: "pie" },
        { name: this.$t("Donut"), value: "donut_chart" },
        { name: this.$t("PolarArea"), value: "polarArea" },
        { name: this.$t("Polar Monochrome"), value: "polarMonochrome" },
        { name: this.$t("Heat Map"), value: "heatmap" },
        { name: this.$t("Scatter"), value: "scatter" },
        { name: this.$t("Radial Bar"), value: "radialBar" },
        { name: this.$t("Area"), value: "area" },
        { name: this.$t("Spline Area"), value: "spline_area" },
        { name: this.$t("Negative Area"), value: "negative_area" },
        // { name: this.$t()"Range Area", value: "rangeArea" },
        // { name: this.$t()"Range Bar", value: "rangeBar" },
        { name: this.$t("Radar"), value: "radar" },
        // { name: this.$t()"BoxPlot", value: "boxPlot" },
        // { name: this.$t()"Candlestick", value: "candlestick" },
        { name: this.$t("Treemap"), value: "treemap" },
        { name: this.$t("Mixed Charts"), value: "mixed_charts" },
        { name: this.$t("Pyramid Chart"), value: "pyramid_chart" },
        { name: this.$t("Funnel Chart"), value: "funnel_chart" },
        { name: this.$t("Stepline Chart"), value: "stepline_chart" },
        { name: this.$t("Gradient Chart"), value: "gradient_chart" }
      ],
      chart_types: [
        { name: this.$t("Column"), value: "column" },
        { name: this.$t("Line"), value: "line" },
        { name: this.$t("Area"), value: "area" }
      ],

      max_limits: [
        { name: "5", value: 5 },
        { name: "10", value: 10 },
        { name: "15", value: 15 },
        { name: "20", value: 20 }
      ],
      truncate_by_types: [
        { name: this.$t("Day"), value: "day" },
        { name: this.$t("Week"), value: "week" },
        { name: this.$t("Month"), value: "month" },
        { name: this.$t("Year"), value: "year" },
        { name: this.$t("Quarter"), value: "quarter" }
      ],

      order_sorts: [
        { name: this.$t("Default"), value: "random" },
        { name: this.$t("Descending"), value: "desc" },
        { name: this.$t("Ascending"), value: "asce" }
      ],

      metrics_aggregation_methods: [
        { name: this.$t("SUM"), value: "sum" },
        { name: this.$t("MIN"), value: "min" },
        { name: this.$t("MAX"), value: "max" },
        { name: this.$t("COUNT"), value: "count" },
        { name: this.$t("AVERAGE"), value: "average" },
        { name: this.$t("WEIGHTED"), value: "weighted" }
      ],
      extra_axis_metrics: [
        {
          reference_name: "history_date",
          id: "history_date",
          name: this.$t("Change date"),
          data_type: "DateTime",
          object_type: this.graph_object_type
        },
        {
          reference_name: "datetime_created",
          id: "datetime_created",
          name: this.$t("Create date"),
          data_type: "DateTime",
          object_type: this.graph_object_type
        },
        {
          reference_name: "parent_id",
          id: "parent_id",
          name: this.$t("Parent"),
          data_type: "String",
          object_type: this.graph_object_type
        },
        {
          reference_name: "owner__id",
          id: "owner__id",
          name: this.$t("Owner"),
          data_type: "String",
          object_type: "record"
        },
        {
          reference_name: "co_owners__id",
          id: "co_owners__id",
          name: this.$t("Co-Owners"),
          data_type: "List",
          object_type: "record"
        }
      ],
      filter: null,
      filter_error: false,
      is_filter_valid: false,
      stacked_bars: false,
      is_loading: false,
      aggregate: false,
      axis_metrics: [],
      series_metrics: [],
      selected_series: []
    }
  },
  created() {
    if (this.graph) {
      this.selected_series = this.graph?.series?.map((serie) => {
        return {
          id: serie.id,
          chart_type: serie.chart_type,
          metric: serie.metric.id ? serie.metric.id : serie.metric,
          aggregation_method: serie.aggregation_method,
          space_color: serie.space_color
        }
      })
      this.aggregate = this.graph.aggregate

      this.graph_object_type = this.graph.type
      this.axis_metrics = this.$filter(this.$store.state.metrics, {
        object_type: this.graph_object_type
      }).concat(this.extra_axis_metrics)
      this.series_metrics = this.axis_metrics.filter((m) => m.data_type == "Number")

      if (utils.filter(this.graph.series, { aggregation_method: "count" }).length > 0) {
        this.series_metrics = utils.clone(this.axis_metrics)
      }
      this.filter = this.graph.filter
    }
  },

  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.filter = parsed_filter.filter
      this.is_filter_valid = parsed_filter.valid
    },
    isDateBasedAxis(values) {
      return (
        values["axis"] &&
        this.axis_metrics.filter((m) => values["axis"] == m.id && (m.data_type == "Date" || m.data_type == "DateTime")).length > 0
      )
    },
    formDataChanged(values) {
      this.$emit("setComponentData", {
        ...values,
        axis: values["axis"] == "history_date" ? null : values["axis"],
        required_filled: values.name && values.axis && (this.filter ? this.is_filter_valid : true),
        selected_series: this.selected_series,
        filter: this.filter,
        is_filter_valid: this.is_filter_valid
      })
    },
    handleSelectAggregation(e, i) {
      if (e) {
        this.selected_series[i]["aggregation_method"] = e
        this.series_metrics = utils.clone(this.axis_metrics)
      }
    },
    async updateGraph(values, { resetForm }) {
      this.filter_error = this.filter && !this.is_filter_valid
      if (this.filter_error) {
        return
      }

      let data = {
        ...values,
        filter: this.filter,
        page: this.pageReference,
        series: this.selected_series
      }
      this.is_loading = true
      if (["history_date", "datetime_created", "parent_id", "owner__id", "co_owners__id"].includes(values["axis"])) {
        data["axis_field"] = data["axis"]
        data["axis"] = null
      }

      await this.$store
        .dispatch("data/updateGraph", { id: this.graph.id, data })
        .then((res) => {
          this.$emit("done")
          this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED, res)
          this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_ADD_NEW_COMPONENTS_MODAL, res)
          this.$emitter.emit(ALL_EMITTERS.GRAPH_UPDATED_IN_GRAPHS_GRID, res)

          if (resetForm) {
            resetForm()
          }
          this.is_loading = false
        })
        .catch((err) => {
          if (err.response.status == 403) {
            this.$toast.open({
              message:
                this.$t("You don't have permission to change the public/private state of graph") + ` <strong>${this.graph.name}</strong>`,
              position: "top",
              type: "error",
              duration: 3000
            })
          }
          this.is_loading = false
        })
    }
  }
}
</script>
<style>
.trans {
  color: transparent;
}
</style>
